import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AnimatedBackground } from '../../components/AnimatedBackground';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';

const CookiePolicyPage: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const isSpanish = i18n.language === 'es';

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="fixed top-0 w-full z-[9999] bg-white/95 backdrop-blur-md border-b border-blue-100 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-blue-700 hover:text-blue-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">{isSpanish ? 'Volver' : 'Back'}</span>
            </button>
            <LanguageSelector variant="compact" className="text-blue-700" />
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <AnimatedBackground variant="hero" className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {isSpanish ? 'Política de Cookies' : 'Cookie Policy'}
            </h1>
            <p className="text-lg text-blue-200">
              {isSpanish
                ? 'Última actualización: Octubre 2025'
                : 'Last updated: October 2025'}
            </p>
          </motion.div>
        </div>
      </AnimatedBackground>

      {/* Content */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-blue max-w-none"
          >
            {isSpanish ? (
              <>
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">¿Qué son las cookies?</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas nuestro sitio web. Estas cookies nos permiten recordar tus preferencias y mejorar tu experiencia de navegación.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Tipos de cookies que utilizamos</h2>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Cookies esenciales</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Son necesarias para el funcionamiento básico del sitio web. Sin estas cookies, el sitio no puede funcionar correctamente.
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                      <li>Gestión de sesiones de usuario</li>
                      <li>Preferencias de idioma</li>
                      <li>Configuración de seguridad</li>
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Cookies de rendimiento</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web, recopilando información de forma anónima.
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                      <li>Google Analytics</li>
                      <li>Análisis de tráfico web</li>
                      <li>Métricas de rendimiento</li>
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Cookies de funcionalidad</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Permiten que el sitio web recuerde las elecciones que realizas para proporcionarte funcionalidades mejoradas y más personales.
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                      <li>Preferencias de visualización</li>
                      <li>Configuración regional</li>
                      <li>Personalización de contenido</li>
                    </ul>
                  </div>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies de terceros</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Utilizamos servicios de terceros que pueden establecer sus propias cookies:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Google Analytics:</strong> Para analizar el uso del sitio web</li>
                    <li><strong>Servicios de redes sociales:</strong> Para compartir contenido en redes sociales</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Cómo gestionar las cookies</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Puedes controlar y/o eliminar las cookies como desees. Puedes eliminar todas las cookies que ya están en tu ordenador y configurar la mayoría de los navegadores para que no se almacenen.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <p className="text-gray-800 font-semibold mb-2">Instrucciones por navegador:</p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li><strong>Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
                      <li><strong>Firefox:</strong> Opciones → Privacidad y seguridad</li>
                      <li><strong>Safari:</strong> Preferencias → Privacidad</li>
                      <li><strong>Edge:</strong> Configuración → Cookies y permisos del sitio</li>
                    </ul>
                  </div>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Ten en cuenta que si deshabilitas las cookies, algunas funcionalidades del sitio web pueden no funcionar correctamente.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Duración de las cookies</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Utilizamos tanto cookies de sesión (que se eliminan cuando cierras el navegador) como cookies persistentes (que permanecen durante un período determinado):
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li><strong>Cookies de sesión:</strong> Se eliminan al cerrar el navegador</li>
                    <li><strong>Cookies de preferencias:</strong> Hasta 1 año</li>
                    <li><strong>Cookies analíticas:</strong> Hasta 2 años</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Actualizaciones de esta política</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Podemos actualizar esta Política de Cookies periódicamente para reflejar cambios en nuestras prácticas o por otras razones operativas, legales o regulatorias.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Contacto</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Si tienes preguntas sobre nuestra Política de Cookies:
                  </p>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-gray-800"><strong>Email:</strong> <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a></p>
                    <p className="text-gray-800"><strong>Teléfono:</strong> +34 608 755 358</p>
                  </div>
                </section>
              </>
            ) : (
              <>
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">What are cookies?</h2>
                  <p className="text-gray-700 leading-relaxed">
                    Cookies are small text files that are stored on your device when you visit our website. These cookies allow us to remember your preferences and improve your browsing experience.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of cookies we use</h2>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Essential cookies</h3>
                    <p className="text-gray-700 leading-relaxed">
                      These are necessary for the basic functioning of the website. Without these cookies, the site cannot function properly.
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                      <li>User session management</li>
                      <li>Language preferences</li>
                      <li>Security settings</li>
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Performance cookies</h3>
                    <p className="text-gray-700 leading-relaxed">
                      They help us understand how visitors interact with our website, collecting information anonymously.
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                      <li>Google Analytics</li>
                      <li>Web traffic analysis</li>
                      <li>Performance metrics</li>
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Functionality cookies</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Allow the website to remember the choices you make to provide you with enhanced and more personalized features.
                    </p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                      <li>Display preferences</li>
                      <li>Regional settings</li>
                      <li>Content personalization</li>
                    </ul>
                  </div>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-party cookies</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    We use third-party services that may set their own cookies:
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2">
                    <li><strong>Google Analytics:</strong> To analyze website usage</li>
                    <li><strong>Social media services:</strong> To share content on social networks</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">How to manage cookies</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    You can control and/or delete cookies as you wish. You can delete all cookies already on your computer and set most browsers not to store them.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg mt-4">
                    <p className="text-gray-800 font-semibold mb-2">Instructions by browser:</p>
                    <ul className="list-disc pl-6 text-gray-700 space-y-1">
                      <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies</li>
                      <li><strong>Firefox:</strong> Options → Privacy and security</li>
                      <li><strong>Safari:</strong> Preferences → Privacy</li>
                      <li><strong>Edge:</strong> Settings → Cookies and site permissions</li>
                    </ul>
                  </div>
                  <p className="text-gray-700 leading-relaxed mt-4">
                    Note that if you disable cookies, some website features may not work properly.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookie duration</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We use both session cookies (which are deleted when you close your browser) and persistent cookies (which remain for a specified period):
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
                    <li><strong>Session cookies:</strong> Deleted when closing the browser</li>
                    <li><strong>Preference cookies:</strong> Up to 1 year</li>
                    <li><strong>Analytics cookies:</strong> Up to 2 years</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to this policy</h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may update this Cookie Policy periodically to reflect changes in our practices or for other operational, legal, or regulatory reasons.
                  </p>
                </section>

                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact</h2>
                  <p className="text-gray-700 leading-relaxed">
                    If you have questions about our Cookie Policy:
                  </p>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-gray-800"><strong>Email:</strong> <a href="mailto:info@scoutmindai.com" className="text-blue-600 hover:text-blue-800">info@scoutmindai.com</a></p>
                    <p className="text-gray-800"><strong>Phone:</strong> +34 608 755 358</p>
                  </div>
                </section>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
